/**
 * Chatwoot SDK 薄封装（C 端客服）
 * 兼容客服桥接提供的 window.ssq / window.ChatwootBridge（SaleSmartly 同名用法）
 */

type ChatwootUserAttrs = {
  name?: string;
  email?: string;
  identifier_hash?: string;
};

type ChatwootApi = {
  toggle: (state?: 'open' | 'close') => void;
  setUser: (identifier: string, userObj: ChatwootUserAttrs) => void;
  reset?: () => void;
};

type ChatwootBridgeApi = {
  sendTextMessage: (text: string, cb?: (result: { status: string }) => void) => void;
  chatOpen: () => void;
  chatClose?: () => void;
  setLoginInfo?: (info: Record<string, unknown>) => void;
  clearUser?: () => void;
  onReady?: (cb: () => void) => void;
};

type SsqApi = {
  push: (command: string, ...args: unknown[]) => void;
};

declare global {
  interface Window {
    $chatwoot?: ChatwootApi;
    chatwootSDK?: {
      run: (options: { websiteToken: string; baseUrl: string }) => void;
    };
    chatwootSettings?: Record<string, unknown>;
    ChatwootBridge?: ChatwootBridgeApi;
    ssq?: SsqApi | unknown[];
  }
}

const READY_POLL_MS = 200;
const READY_MAX_WAIT_MS = 8000;

function runWhenChatwootReady(fn: () => void): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.$chatwoot) {
    fn();
    return;
  }
  const started = Date.now();
  const timer = window.setInterval(() => {
    if (window.$chatwoot) {
      window.clearInterval(timer);
      fn();
      return;
    }
    if (Date.now() - started >= READY_MAX_WAIT_MS) {
      window.clearInterval(timer);
    }
  }, READY_POLL_MS);
}

/** 兼容桥接：优先 ChatwootBridge 直调，否则走 ssq.push（可早于就绪排队） */
function pushSsq(command: string, ...args: unknown[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  const bridge = window.ChatwootBridge;
  if (bridge) {
    if (command === 'sendTextMessage' && typeof bridge.sendTextMessage === 'function') {
      const text = typeof args[0] === 'string' ? args[0] : '';
      const cb = typeof args[1] === 'function' ? (args[1] as (result: { status: string }) => void) : undefined;
      bridge.sendTextMessage(text, cb);
      return;
    }
    if (command === 'chatOpen' && typeof bridge.chatOpen === 'function') {
      bridge.chatOpen();
      return;
    }
    if (command === 'chatClose' && typeof bridge.chatClose === 'function') {
      bridge.chatClose();
      return;
    }
    if (command === 'clearUser' && typeof bridge.clearUser === 'function') {
      bridge.clearUser();
      return;
    }
    if (command === 'setLoginInfo' && typeof bridge.setLoginInfo === 'function') {
      bridge.setLoginInfo((args[0] as Record<string, unknown>) || {});
      return;
    }
    if (command === 'onReady' && typeof bridge.onReady === 'function') {
      bridge.onReady(args[0] as () => void);
      return;
    }
  }

  if (!window.ssq) {
    window.ssq = [];
  }
  const q = window.ssq as SsqApi & unknown[];
  if (typeof q.push === 'function') {
    q.push(command, ...args);
  }
}

/** 打开 Chatwoot 聊天气泡 */
export function openChatwoot(): void {
  if (typeof window !== 'undefined' && (window.ChatwootBridge?.chatOpen || window.ssq)) {
    pushSsq('chatOpen');
    return;
  }
  runWhenChatwootReady(() => {
    window.$chatwoot?.toggle('open');
  });
}

/** 代发一条访客文字消息（聊天窗会自动展开；单次发送，不做重试以免 2s 去重 ignored） */
export function sendChatwootTextMessage(text: string): void {
  const message = text?.trim();
  if (!message) {
    return;
  }
  pushSsq('sendTextMessage', message);
}

/** 登录后同步用户身份（需后端签发的 identifier_hash） */
export function syncChatwootUser(uid: string, name: string, identifierHash: string): void {
  if (!uid || !identifierHash) {
    return;
  }
  runWhenChatwootReady(() => {
    window.$chatwoot?.setUser(uid, {
      name,
      identifier_hash: identifierHash,
    });
  });
}

/** 退出登录时重置 Chatwoot 会话身份 */
export function resetChatwootUser(): void {
  runWhenChatwootReady(() => {
    window.$chatwoot?.reset?.();
  });
}
